import type React from "react";
import { useState } from "react";
import { useTranslation } from "@/packages/utils";
import type { ErrorModalProps, ErrorReportDetails } from "./types";

export const ErrorModal: React.FC<ErrorModalProps> = ({
  isOpen,
  onClose,
  error,
  errorInfo,
  context,
  onReport,
  onRetry,
  showTechnicalDetails = false,
}) => {
  const { t } = useTranslation(["common"]);
  const [userDescription, setUserDescription] = useState("");
  const [userEmail, setUserEmail] = useState("");
  const [includeScreenshots, setIncludeScreenshots] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [activeTab, setActiveTab] = useState<"overview" | "technical">(
    "overview",
  );

  const handleSubmit = async () => {
    if (!onReport) return;

    setIsSubmitting(true);
    try {
      const reportDetails: ErrorReportDetails = {
        error,
        errorInfo,
        context,
        userDescription,
        userEmail,
        includeScreenshots,
      };

      await onReport(reportDetails);
      onClose();
    } catch (reportError) {
      console.error("Failed to submit error report:", reportError);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRetry = () => {
    onRetry?.();
    onClose();
  };

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleOverlayKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Escape") {
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="error-modal-overlay fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="error-modal-title"
      onClick={handleOverlayClick}
      onKeyDown={handleOverlayKeyDown}
    >
      <div className="error-modal bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="error-modal-header flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <h2
            id="error-modal-title"
            className="error-modal-title text-xl font-semibold text-gray-900 dark:text-gray-100"
          >
            {t("common:errorModal.title")}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="error-modal-close p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
            aria-label="Close error modal"
          >
            <svg
              className="h-6 w-6"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              role="img"
              aria-label="Close icon"
            >
              <title>Close</title>
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        {/* Body */}
        <div className="error-modal-body flex-1 overflow-y-auto">
          {/* Tabs */}
          <div className="error-modal-tabs flex border-b border-gray-200 dark:border-gray-700">
            <button
              type="button"
              onClick={() => setActiveTab("overview")}
              className={`tab px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === "overview"
                  ? "border-blue-500 text-blue-600 dark:text-blue-400"
                  : "border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300"
              }`}
            >
              {t("common:errorModal.overview")}
            </button>
            {showTechnicalDetails && (
              <button
                type="button"
                onClick={() => setActiveTab("technical")}
                className={`tab px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === "technical"
                    ? "border-blue-500 text-blue-600 dark:text-blue-400"
                    : "border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300"
                }`}
              >
                {t("common:errorModal.technical")}
              </button>
            )}
          </div>

          {/* Tab Content */}
          <div className="p-6">
            {activeTab === "overview" && (
              <div className="error-overview space-y-6">
                {/* Error Summary */}
                <div className="error-summary flex items-start space-x-4">
                  <div className="error-icon flex-shrink-0">
                    <svg
                      className="h-8 w-8 text-red-500"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      role="img"
                      aria-label="Error icon"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                      />
                    </svg>
                  </div>
                  <div className="error-message flex-1">
                    <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
                      {t("common:errorModal.somethingWentWrong")}
                    </h3>
                    <p className="text-gray-600 dark:text-gray-400 mb-2">
                      {error.message}
                    </p>
                    {context?.correlationId && (
                      <p className="error-id text-sm text-gray-500 dark:text-gray-500">
                        Error ID:{" "}
                        <code className="bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded text-xs">
                          {context.correlationId}
                        </code>
                      </p>
                    )}
                  </div>
                </div>

                {/* Error Report Form */}
                {onReport && (
                  <div className="error-report-form space-y-4">
                    <div>
                      <h4 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
                        {t("common:errorModal.helpUsImprove")}
                      </h4>
                      <p className="text-gray-600 dark:text-gray-400 text-sm mb-4">
                        {t("common:errorModal.descriptionPrompt")}
                      </p>
                    </div>

                    <div className="form-group">
                      <label
                        htmlFor="error-description"
                        className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
                      >
                        {t("common:errorModal.descriptionLabel")}
                      </label>
                      <textarea
                        id="error-description"
                        value={userDescription}
                        onChange={(e) => setUserDescription(e.target.value)}
                        placeholder={t(
                          "common:errorModal.descriptionPlaceholder",
                        )}
                        rows={4}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-gray-100"
                      />
                    </div>

                    <div className="form-group">
                      <label
                        htmlFor="user-email"
                        className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
                      >
                        {t("common:errorModal.emailLabel")}
                      </label>
                      <input
                        id="user-email"
                        type="email"
                        value={userEmail}
                        onChange={(e) => setUserEmail(e.target.value)}
                        placeholder="your@email.com"
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-gray-100"
                      />
                    </div>

                    <div className="form-group">
                      <label className="flex items-center space-x-2 text-sm text-gray-700 dark:text-gray-300">
                        <input
                          type="checkbox"
                          checked={includeScreenshots}
                          onChange={(e) =>
                            setIncludeScreenshots(e.target.checked)
                          }
                          className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-700"
                        />
                        <span>{t("common:errorModal.includeScreenshots")}</span>
                      </label>
                    </div>
                  </div>
                )}
              </div>
            )}

            {activeTab === "technical" && showTechnicalDetails && (
              <div className="error-technical space-y-4">
                <div className="technical-details">
                  <h4 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">
                    {t("common:errorModal.technicalInfo")}
                  </h4>

                  <div className="space-y-4">
                    <div className="detail-section">
                      <h5 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        {t("common:errorModal.error")}
                      </h5>
                      <pre className="error-stack bg-gray-100 dark:bg-gray-700 p-3 rounded-md text-xs font-mono overflow-x-auto whitespace-pre-wrap">
                        {error.stack}
                      </pre>
                    </div>

                    {errorInfo && (
                      <div className="detail-section">
                        <h5 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          {t("common:errorModal.componentStack")}
                        </h5>
                        <pre className="component-stack bg-gray-100 dark:bg-gray-700 p-3 rounded-md text-xs font-mono overflow-x-auto whitespace-pre-wrap">
                          {errorInfo.componentStack}
                        </pre>
                      </div>
                    )}

                    {context && (
                      <div className="detail-section">
                        <h5 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          {t("common:errorModal.context")}
                        </h5>
                        <pre className="context-info bg-gray-100 dark:bg-gray-700 p-3 rounded-md text-xs font-mono overflow-x-auto whitespace-pre-wrap">
                          {JSON.stringify(context, null, 2)}
                        </pre>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="error-modal-footer border-t border-gray-200 dark:border-gray-700 p-6">
          <div className="footer-actions flex flex-col sm:flex-row gap-3 justify-end">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-md transition-colors"
            >
              {t("common:buttons.close")}
            </button>

            {onRetry && (
              <button
                type="button"
                onClick={handleRetry}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors"
              >
                {t("common:errors.tryAgain")}
              </button>
            )}

            {onReport && (
              <button
                type="button"
                onClick={handleSubmit}
                disabled={isSubmitting}
                className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {isSubmitting
                  ? t("common:errorModal.submitting")
                  : t("common:errorModal.sendReport")}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ErrorModal;
